import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Header } from "@ui/components/Header";
import { Footer } from '@ui/components/Footer';
import { useEffect, useState } from "react";
import { getSupabaseClient } from '../lib/supabase';

// Custom hook for user profile
function useUserProfile() {
  const [userProfile, setUserProfile] = useState<{
    name?: string;
    role?: string;
    avatar?: string;
  }>({});

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const supabase = getSupabaseClient();

        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) return;

        // Fetch profile data
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        // Fetch enhanced profile for avatar
        const { data: enhancedProfile } = await supabase
          .from('guides_enhanced_profile')
          .select('image_url')
          .eq('guide_id', user.id)
          .single();

        if (!profileError && profile) {
          setUserProfile({
            name: profile.full_name || 'Guide',
            role: 'Guide',
            avatar: enhancedProfile?.image_url || '/gclogo.png'
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, []);

  return userProfile;
}

const HONOR_RULES = [
  'Never discuss substance related transactions',
  'While we never store chat or video information, be mindful of conversations',
  'Keep all conversations inside of the platform',
  'Don\'t share client\'s private information',
];

export default function FullApproval() {
  const userProfile = useUserProfile();
  const [checked, setChecked] = useState<boolean[]>(HONOR_RULES.map(() => false));
  const [showError, setShowError] = useState(false);

  const allChecked = checked.every(Boolean);

  const toggleRule = (index: number) => {
    setChecked((prev) => prev.map((v, i) => (i === index ? !v : v)));
    setShowError(false);
  };

  const handleContinue = () => {
    if (!allChecked) {
      setShowError(true);
      return;
    }
    window.location.href = '/dashboard';
  };

  return (
    <div className="min-h-screen bg-[#fffcef] flex flex-col">
      <Header isLoggedIn={true} userProfile={userProfile} />
    <div className="max-w-2xl mx-auto text-center py-20 px-4">

      <div className="flex justify-center mb-8">
        <div className="h-20 w-20 rounded-full bg-[#304031]/10 flex items-center justify-center">
          <CheckCircleIcon className="h-10 w-10 text-[#304031]" />
        </div>
      </div>

      <h1 className="mb-3 text-center font-recoleta text-[42px] leading-[44px] font-medium text-[#304031]">
        Congratulations!
      </h1>
      <p className="text-[#304031]/70 mb-12 text-lg">
        Your guide account has been approved and published. You can now start
        managing seekers and building your community.
      </p>

      {/* Honor Policy */}
      <div className="bg-[#fffcef] border-2 border-[#a0978a] rounded-2xl p-8 text-left mb-10 shadow-sm">
        <p className="font-semibold text-[#304031] mb-6 text-lg">
          Before you get started, please review our honor policy
        </p>
        <div className="space-y-4 text-sm text-[#304031]">
          {HONOR_RULES.map((rule, index) => (
            <label key={rule} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checked[index]}
                onChange={() => toggleRule(index)}
                className="mt-1 h-5 w-5 rounded border-2 border-[#a0978a] text-[#304031] focus:ring-2 focus:ring-[#304031]/20"
              />
              <span>{rule}</span>
            </label>
          ))}
        </div>
        {showError && (
          <p className="mt-4 text-sm font-medium text-red-600">
            You must agree to all points before accessing the platform.
          </p>
        )}
      </div>
      {/* Go to Dashboard Button */}
      <button
        className={`inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full transition-colors mb-8 w-full max-w-xl mx-auto font-semibold ${
          allChecked
            ? 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
            : 'bg-[#304031]/30 text-[#304031]/50 cursor-not-allowed'
        }`}
        onClick={handleContinue}
      >
        <CheckCircleIcon className="h-5 w-5" />
        Go to Dashboard
      </button>

      {/* Footer links */}
      <p className="text-sm text-[#304031]/70 mb-4">Ready to start your journey as a guide?</p>
      <div className="flex justify-center gap-6 text-sm text-[#304031] font-medium">
        <a href="#" className="hover:underline">
          Getting Started Guide
        </a>
        <a href="#" className="hover:underline">
          Best Practices
        </a>
        <a href="#" className="hover:underline">
          Community Guidelines
        </a>
      </div>
    </div>
    {/* Footer */}
          <Footer />
    </div>
  );
}
